import { Controller } from "@hotwired/stimulus"

// The browser half of a passkey ceremony.
//
// It has to be JS and it cannot be a form: only `navigator.credentials` can
// talk to the authenticator, and what it hands back is ArrayBuffers, which no
// form encodes. So the shape is always the same three steps — ask this app
// for a challenge, have the authenticator sign it, post the signature back —
// and the only difference between making a passkey and using one is `create`
// versus `get`. Both are here because splitting them would duplicate the
// base64url plumbing, which is most of the file.
//
// The server end is Fire::PasskeysController; the wire format is base64url
// both ways, which is what the WebAuthn spec encodes with and what the gem's
// default encoder reads. The host registers it as `passkey`:
//
//   import PasskeyController from "@fire/passkey_controller"
//   application.register("passkey", PasskeyController)
export default class extends Controller {
  static targets = ["button", "error"]
  static values = {
    challenge: String, // where to ask for one
    verify: String, // where to send what the authenticator signed
    mode: { type: String, default: "signin" }, // "signin" | "create"
    next: String, // where a fresh passkey lands; reload if the host says nothing
    say: Object, // { failed, unavailable } in the page's language (AuthDoorsHelper#passkey_say)
  }

  // A browser with no WebAuthn gets no button. A key-shaped thing that does
  // nothing when pressed is worse than an email field.
  //
  // And when it does have one, the block says so in the DOM: the button is
  // markup long before Stimulus reaches it, and a press that lands in between
  // does nothing whatsoever — on a slow connection to a person, and every
  // time to a system spec, which clicks the instant the HTML is there.
  //
  // The /me flash is drawn `hidden` and this is what shows it: only a browser
  // that can make a passkey ever sees the ask, not even for a frame.
  connect() {
    this.element.hidden = !window.PublicKeyCredential
    if (this.element.hidden) return

    this.element.dataset.passkeyReady = "true"
    if (this.modeValue === "signin") this.autofill()
  }

  disconnect() {
    this.pending?.abort()
  }

  // Autofill: the passkey offered in the login field's own dropdown, beside
  // saved passwords — any field marked `autocomplete="username webauthn"`
  // (fire/shared/_login). A quiet `get` that waits for the person to pick
  // one; the button aborts it, since a page holds only one `get` at a time.
  // Quiet when the browser refuses: she pressed nothing, so red text under
  // a button she never touched is noise — an IP host in a spec, or an rp_id
  // that does not match the address, refuses here on every page load.
  //
  // ponytail: one challenge, fetched on load, lives CHALLENGE_TTL (3 min) —
  // picked later, it answers "expired". Refetch on focus if that bites.
  async autofill() {
    if (!(await PublicKeyCredential.isConditionalMediationAvailable?.())) return

    try {
      await this.ceremony({ mediation: "conditional" })
    } catch (error) {
      // Only the server's answer: by then she did pick a passkey.
      if (error.said) this.say(error.message)
    }
  }

  // "Agora não": the flash's quiet dismiss. A cookie, 30 days, every path, per
  // browser on purpose (a passkey lives on a device); the server reads it, so
  // the flash is never drawn again, and the element leaves the DOM so a Turbo
  // snapshot of this page does not bring it back.
  later() {
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `passkey_later=1; Max-Age=${30 * 24 * 60 * 60}; Path=/; SameSite=Lax${secure}`
    this.element.remove()
  }

  async run() {
    this.say(null)
    this.busy(true)

    try {
      await this.ceremony()
    } catch (error) {
      this.busy(false)
      // NotAllowedError is the person closing the prompt, and AbortError is
      // the browser doing it for them. Neither is a failure, and neither
      // deserves red text under the button.
      if (error.name === "NotAllowedError" || error.name === "AbortError") return
      this.say(this.words(error))
    }
  }

  // The server's own message is already in her language. The browser's is
  // English and technical: a refused origin or a missing authenticator says
  // passkeys do not work here, anything else says try again.
  words(error) {
    if (error.said) return error.message
    const say = this.sayValue
    const key = ["SecurityError", "NotSupportedError"].includes(error.name)
      ? "unavailable"
      : "failed"
    return say[key] || say.failed || error.message
  }

  // Ask, sign, post back, go. `extra` is the autofill's `mediation`.
  async ceremony(extra = {}) {
    this.pending?.abort()
    this.pending = new AbortController()
    const signal = this.pending.signal

    const options = await this.post(this.challengeValue)
    const credential =
      this.modeValue === "create"
        ? await navigator.credentials.create({ publicKey: this.creation(options), signal })
        : await navigator.credentials.get({ publicKey: this.request(options), signal, ...extra })

    const { to } = await this.post(this.verifyValue, { credential: this.wire(credential) })

    // A full load, not Turbo: signing in changes the session, and a page
    // that just grew a passkey has a shelf on it that is now wrong.
    //
    // Where to is the SERVER's for a sign-in — it is the only end that
    // knows who just arrived — and the host template's for a fresh
    // passkey. Never a path off the wire mixed with one off the page:
    // that is an open redirect with extra steps.
    //
    // In a Hotwire Native shell the sign-in page is pushed over a stale root,
    // so a sign-in visits /in, which the path configuration replaces the whole
    // stack for (docs/DOORS.md § Sign in inside the shell).
    if (to && this.inShell) return window.Turbo.visit("/in", { action: "replace" })
    window.location.assign(to || this.nextValue || window.location.href)
  }

  // The user agent turbo-rails reads on the server (hotwire_native_app?).
  get inShell() {
    return /(Turbo|Hotwire) Native/.test(navigator.userAgent)
  }

  // ── The wire ──────────────────────────────────────────────────────────

  async post(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
      },
      credentials: "same-origin",
      body: JSON.stringify(body || {}),
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok)
      throw Object.assign(new Error(json.error || response.statusText), { said: !!json.error })
    return json
  }

  // What the server sent, with every id turned back into bytes. The three
  // fields that are base64url on the wire and buffers in the API are the
  // challenge, the user id, and each id in the exclude list.
  creation(options) {
    return {
      ...options,
      challenge: decode(options.challenge),
      user: { ...options.user, id: decode(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((c) => ({ ...c, id: decode(c.id) })),
    }
  }

  request(options) {
    return {
      ...options,
      challenge: decode(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: decode(c.id) })),
    }
  }

  // The credential, flattened for JSON. `response` is a live object with
  // getters rather than a plain one, so the fields are named out here — a
  // spread would post `{}`.
  wire(credential) {
    const r = credential.response
    const body = {
      id: credential.id,
      type: credential.type,
      rawId: encode(credential.rawId),
      clientExtensionResults: credential.getClientExtensionResults(),
      response: { clientDataJSON: encode(r.clientDataJSON) },
    }

    if (r.attestationObject) {
      body.response.attestationObject = encode(r.attestationObject)
    } else {
      body.response.authenticatorData = encode(r.authenticatorData)
      body.response.signature = encode(r.signature)
      body.response.userHandle = r.userHandle ? encode(r.userHandle) : null
    }

    return body
  }

  // ── The button ────────────────────────────────────────────────────────

  busy(on) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = on
  }

  say(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message || ""
    this.errorTarget.hidden = !message
  }
}

// base64url, both directions. Not base64: the spec's encoding is the URL
// alphabet with the padding dropped, and atob/btoa know neither.
const decode = (value) => {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/")
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="))
  return Uint8Array.from(binary, (c) => c.charCodeAt(0))
}

const encode = (buffer) => {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  // A byte at a time, because an attestation object is kilobytes and
  // `String.fromCharCode(...bytes)` blows the argument limit on some of them.
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
}
